export const CONVERSATION_REPORT_EXPORT_LIMIT = 1000;

export type ConversationReportStatus = "attending" | "waiting" | "closed" | "reactivated";

export type ConversationReportSortDirection = "asc" | "desc";

export type ConversationReportQuery = {
  id?: number;
  contactText?: string;
  mobilePhone?: string;
  platformId?: number;
  userId?: number;
  workflowId?: number;
  customerServiceQueueId?: number;
  mainSubjectId?: number;
  conversationType?: string;
  hasUnread?: boolean;
  closedByHuman?: boolean;
  supportByHuman?: boolean;
  issueSolved?: boolean;
  status?: ConversationReportStatus;
  startDateFrom?: string;
  startDateTo?: string;
  lastReceivedDateFrom?: string;
  lastReceivedDateTo?: string;
  lastReadDateFrom?: string;
  lastReadDateTo?: string;
  lastInterationDateFrom?: string;
  lastInterationDateTo?: string;
  firstAnswerDateFrom?: string;
  firstAnswerDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  lastReactivationAtFrom?: string;
  lastReactivationAtTo?: string;
  deletedDateFrom?: string;
  deletedDateTo?: string;
  csat?: number;
  hasCsat?: boolean;
  sortBy?: string;
  sortDirection?: ConversationReportSortDirection;
};

export type ConversationReportForm = {
  contactText: string;
  platformId: string;
  workflowId: string;
  customerServiceQueueId: string;
  mainSubjectId: string;
  status: string;
  hasUnread: string;
  supportByHuman: string;
  closedByHuman: string;
  issueSolved: string;
  conversationType: string;
  startDateFrom: string;
  startDateTo: string;
  lastReceivedDateFrom: string;
  lastReceivedDateTo: string;
  lastInterationDateFrom: string;
  lastInterationDateTo: string;
  firstAnswerDateFrom: string;
  firstAnswerDateTo: string;
  endDateFrom: string;
  endDateTo: string;
  lastReactivationAtFrom: string;
  lastReactivationAtTo: string;
  mobilePhone: string;
  userId: string;
  id: string;
  lastReadDateFrom: string;
  lastReadDateTo: string;
  deletedDateFrom: string;
  deletedDateTo: string;
  csat: string;
  sortBy: string;
  sortDirection: ConversationReportSortDirection;
};

export const EMPTY_CONVERSATION_REPORT_FORM: ConversationReportForm = {
  contactText: "",
  platformId: "",
  workflowId: "",
  customerServiceQueueId: "",
  mainSubjectId: "",
  status: "",
  hasUnread: "",
  supportByHuman: "",
  closedByHuman: "",
  issueSolved: "",
  conversationType: "",
  startDateFrom: "",
  startDateTo: "",
  lastReceivedDateFrom: "",
  lastReceivedDateTo: "",
  lastInterationDateFrom: "",
  lastInterationDateTo: "",
  firstAnswerDateFrom: "",
  firstAnswerDateTo: "",
  endDateFrom: "",
  endDateTo: "",
  lastReactivationAtFrom: "",
  lastReactivationAtTo: "",
  mobilePhone: "",
  userId: "",
  id: "",
  lastReadDateFrom: "",
  lastReadDateTo: "",
  deletedDateFrom: "",
  deletedDateTo: "",
  csat: "",
  sortBy: "lastReceivedDate",
  sortDirection: "desc",
};

export interface IConversationReportRow {
  id: number;
  contactId: number;
  platformId: number | null;
  contactName: string | null;
  mobilePhone: string | null;
  platformName: string | null;
  userId: number | null;
  userName: string | null;
  workflowId: number | null;
  workflowName: string | null;
  customerServiceQueueId: number | null;
  queueName: string | null;
  mainSubjectId: number | null;
  mainSubjectName: string | null;
  startDate: string | null;
  lastReceivedDate: string | null;
  lastInterationDate: string | null;
  firstAnswerDate: string | null;
  endDate?: string | null;
  closedByHuman?: boolean | null;
  unReadMessages: number;
  reactivationCount: number;
  csat: number | null;
  issueSolved: boolean | null;
  status: ConversationReportStatus;
}

const optionalNumber = (value: string): number | undefined => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const optionalBool = (value: string): boolean | undefined => {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};

const optionalText = (value: string): string | undefined => {
  const text = value.trim();
  return text || undefined;
};

const optionalDate = (value: string): string | undefined => value || undefined;

function csatFilter(value: string): Pick<ConversationReportQuery, "csat" | "hasCsat"> {
  if (value === "none") return { hasCsat: false };
  const score = Number(value);
  if (Number.isInteger(score) && score >= 1 && score <= 5) return { csat: score };
  return {};
}

export function toConversationReportQuery(form: ConversationReportForm): ConversationReportQuery {
  return {
    contactText: optionalText(form.contactText),
    mobilePhone: optionalText(form.mobilePhone),
    platformId: optionalNumber(form.platformId),
    userId: optionalNumber(form.userId),
    workflowId: optionalNumber(form.workflowId),
    customerServiceQueueId: optionalNumber(form.customerServiceQueueId),
    mainSubjectId: optionalNumber(form.mainSubjectId),
    hasUnread: optionalBool(form.hasUnread),
    closedByHuman: optionalBool(form.closedByHuman),
    supportByHuman: optionalBool(form.supportByHuman),
    issueSolved: optionalBool(form.issueSolved),
    status: (optionalText(form.status) as ConversationReportStatus | undefined) ?? undefined,
    startDateFrom: optionalDate(form.startDateFrom),
    startDateTo: optionalDate(form.startDateTo),
    lastReceivedDateFrom: optionalDate(form.lastReceivedDateFrom),
    lastReceivedDateTo: optionalDate(form.lastReceivedDateTo),
    lastReadDateFrom: optionalDate(form.lastReadDateFrom),
    lastReadDateTo: optionalDate(form.lastReadDateTo),
    lastInterationDateFrom: optionalDate(form.lastInterationDateFrom),
    lastInterationDateTo: optionalDate(form.lastInterationDateTo),
    firstAnswerDateFrom: optionalDate(form.firstAnswerDateFrom),
    firstAnswerDateTo: optionalDate(form.firstAnswerDateTo),
    endDateFrom: optionalDate(form.endDateFrom),
    endDateTo: optionalDate(form.endDateTo),
    lastReactivationAtFrom: optionalDate(form.lastReactivationAtFrom),
    lastReactivationAtTo: optionalDate(form.lastReactivationAtTo),
    deletedDateFrom: optionalDate(form.deletedDateFrom),
    deletedDateTo: optionalDate(form.deletedDateTo),
    ...csatFilter(form.csat),
    sortBy: form.sortBy || "lastReceivedDate",
    sortDirection: form.sortDirection || "desc",
  };
}
